import React, { useEffect, useState } from 'react';
import {
  Alert,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  InputAdornment,
  TextField,
  Typography,
} from '@mui/material';
import { Edit as EditIcon, Info as InfoIcon } from '@mui/icons-material';
import api from '../../services/api';
import contactService from '../../services/contactService';
import { fetchSenderInboundRate, setContactInboundRate } from '../../services/contactIdentityCache';
import { UpdateCategoryFeeRequest, UpdateSenderFeeRequest } from '../../types';

export interface AdjustSenderRateDialogProps {
  open: boolean;
  onClose: () => void;
  username: string;
  fromName?: string;
  fromAddress: string;
  commercialMailing?: boolean;
  commercialCategory?: string;
  actualPostageReceived: number;
  onSaved?: (rate: number) => void;
}

export const AdjustSenderRateDialog: React.FC<AdjustSenderRateDialogProps> = ({
  open,
  onClose,
  username,
  fromName,
  fromAddress,
  commercialMailing,
  commercialCategory,
  actualPostageReceived,
  onSaved,
}) => {
  const [newPostageRate, setNewPostageRate] = useState('0.05');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open || !fromAddress) return;

    let cancelled = false;
    const fallbackRate = actualPostageReceived;
    setNewPostageRate(fallbackRate.toFixed(2));
    setError(null);

    (async () => {
      try {
        const liveRate = await fetchSenderInboundRate(username, fromAddress);
        if (!cancelled) {
          setNewPostageRate(liveRate.toFixed(2));
        }
      } catch {
        if (!cancelled) {
          setNewPostageRate('0.00');
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [open, fromAddress, username, actualPostageReceived]);

  const handleSave = async () => {
    const rate = parseFloat(newPostageRate);
    if (isNaN(rate) || rate < 0) {
      setError('Invalid postage rate');
      return;
    }

    setSaving(true);
    setError(null);
    try {
      if (!commercialMailing) {
        await contactService.adjustSenderPostage({
          senderAddress: fromAddress,
          newPostageRequirement: rate,
        });
        setContactInboundRate(username, fromAddress, rate);
      } else {
        const request: UpdateSenderFeeRequest = {
          senderAddress: fromAddress,
          deliveryFee: rate,
          refuseMail: false,
        };
        await api.put('/api/commercialfees/sender', request);
      }

      onSaved?.(rate);
      onClose();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to adjust sender postage rate');
    } finally {
      setSaving(false);
    }
  };

  const senderLabel = fromName || fromAddress?.split('@')[0] || 'Sender';

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <EditIcon color="primary" />
        Adjust Sender-Specific Rate
      </DialogTitle>
      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        <Alert severity="info" sx={{ mb: 2 }}>
          <Typography variant="body2">
            Set a custom postage rate for <strong>{senderLabel}</strong>.
          </Typography>
          <Typography variant="body2" sx={{ mt: 1 }}>
            This message was delivered for <strong>${actualPostageReceived.toFixed(2)}</strong>.
          </Typography>
        </Alert>

        <TextField
          fullWidth
          label="Postage Required from This Sender"
          type="number"
          value={newPostageRate}
          onChange={(e) => setNewPostageRate(e.target.value)}
          InputProps={{
            startAdornment: <InputAdornment position="start">$</InputAdornment>,
          }}
          inputProps={{ min: 0, step: 0.01 }}
          sx={{ mb: 2 }}
        />

        <Alert severity="warning" icon={<InfoIcon />}>
          <Typography variant="caption" component="div">
            <strong>Sender Rate Override:</strong>
          </Typography>
          <Typography variant="caption" component="div" sx={{ mt: 0.5 }}>
            • This rate applies only to <strong>this specific sender</strong>
          </Typography>
          {commercialMailing && (
            <Typography variant="caption" component="div">
              • This will <strong>override</strong> the "{commercialCategory || 'Marketing'}" category rate for this sender
            </Typography>
          )}
          <Typography variant="caption" component="div">
            • The sender will be added to your address book if not already present
          </Typography>
        </Alert>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>
          Cancel
        </Button>
        <Button variant="contained" onClick={handleSave} disabled={saving} color="primary">
          {saving ? <CircularProgress size={20} /> : 'Set Sender Rate'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export interface AdjustCategoryRateDialogProps {
  open: boolean;
  onClose: () => void;
  commercialCategory?: string;
  commercialCode?: string;
  initialRate: number;
  onSaved?: (rate: number) => void;
}

export const AdjustCategoryRateDialog: React.FC<AdjustCategoryRateDialogProps> = ({
  open,
  onClose,
  commercialCategory,
  commercialCode,
  initialRate,
  onSaved,
}) => {
  const [newCategoryRate, setNewCategoryRate] = useState('0.05');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setNewCategoryRate(initialRate.toFixed(2));
    setError(null);
  }, [open, initialRate]);

  const handleSave = async () => {
    const rate = parseFloat(newCategoryRate);
    if (isNaN(rate) || rate < 0) {
      setError('Invalid category rate');
      return;
    }

    if (!commercialCode) {
      setError('No commercial category code found for this message');
      return;
    }

    setSaving(true);
    setError(null);
    try {
      const request: UpdateCategoryFeeRequest = {
        commercialCode,
        deliveryFee: rate,
        refuseMail: false,
      };
      await api.put('/api/commercialfees/category', request);
      onSaved?.(rate);
      onClose();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to adjust category rate');
    } finally {
      setSaving(false);
    }
  };

  const categoryLabel = commercialCategory || 'Marketing';

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <EditIcon color="info" />
        Adjust "{categoryLabel}" Category Rate
      </DialogTitle>
      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        <Alert severity="info" sx={{ mb: 2 }}>
          <Typography variant="body2">
            Set the default postage rate for <strong>all senders</strong> in the "{categoryLabel}" category.
          </Typography>
        </Alert>

        <TextField
          fullWidth
          label="Category Postage Rate"
          type="number"
          value={newCategoryRate}
          onChange={(e) => setNewCategoryRate(e.target.value)}
          InputProps={{
            startAdornment: <InputAdornment position="start">$</InputAdornment>,
          }}
          inputProps={{ min: 0, step: 0.01 }}
          sx={{ mb: 2 }}
        />

        <Alert severity="warning" icon={<InfoIcon />}>
          <Typography variant="caption" component="div">
            <strong>Category Rate vs. Sender Rate:</strong>
          </Typography>
          <Typography variant="caption" component="div" sx={{ mt: 0.5 }}>
            • This rate applies to <strong>all senders</strong> in the "{categoryLabel}" category
          </Typography>
          <Typography variant="caption" component="div">
            • If a sender is in your address book with a custom rate, their <strong>sender rate takes priority</strong>
          </Typography>
          <Typography variant="caption" component="div">
            • New senders in this category will use this rate until you set a sender-specific rate
          </Typography>
        </Alert>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>
          Cancel
        </Button>
        <Button variant="contained" onClick={handleSave} disabled={saving} color="info">
          {saving ? <CircularProgress size={20} /> : 'Set Category Rate'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
